import React, { useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, ClockIcon, DocumentTextIcon, ArrowPathIcon, PlayIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import useAuditLogStore from '../stores/auditLogStore';

interface AuditLog {
  id: string;
  action: 'CREATED' | 'STARTED' | 'COMPLETED' | 'FAILED' | 'REOPENED' | 'NOTES_UPDATED' | 'STATUS_CHANGED';
  description: string;
  previousData?: any;
  newData?: any;
  performedBy: string;
  performedAt: string;
}

interface PeriodClosingTimelineProps {
  periodClosingId: string;
  className?: string;
}

const PeriodClosingTimeline: React.FC<PeriodClosingTimelineProps> = ({
  periodClosingId,
  className = ''
}) => {
  const {
    auditLogs,
    loading,
    error,
    fetchAuditLogs
  } = useAuditLogStore();

  // 取得此期間結算的審計日誌
  const currentAuditLogs = auditLogs[periodClosingId] || [];
  const isLoading = loading[periodClosingId] || false;

  useEffect(() => {
    if (periodClosingId) {
      fetchAuditLogs(periodClosingId);
    }
  }, [periodClosingId, fetchAuditLogs]);

  // 錯誤處理
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // 根據動作類型獲取圖示和樣式
  const getActionIcon = (action: AuditLog['action']) => {
    switch (action) {
      case 'CREATED':
        return {
          icon: PlayIcon,
          bgColor: 'bg-blue-100 dark:bg-blue-900/20',
          iconColor: 'text-blue-600 dark:text-blue-400'
        };
      case 'STARTED':
        return {
          icon: ClockIcon,
          bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
          iconColor: 'text-yellow-600 dark:text-yellow-400'
        };
      case 'COMPLETED':
        return {
          icon: CheckCircleIcon,
          bgColor: 'bg-green-100 dark:bg-green-900/20',
          iconColor: 'text-green-600 dark:text-green-400'
        };
      case 'FAILED':
        return {
          icon: XCircleIcon,
          bgColor: 'bg-red-100 dark:bg-red-900/20',
          iconColor: 'text-red-600 dark:text-red-400'
        };
      case 'REOPENED':
        return {
          icon: ArrowPathIcon,
          bgColor: 'bg-purple-100 dark:bg-purple-900/20',
          iconColor: 'text-purple-600 dark:text-purple-400'
        };
      case 'NOTES_UPDATED':
        return {
          icon: DocumentTextIcon,
          bgColor: 'bg-brand-100 dark:bg-brand-900/20',
          iconColor: 'text-brand-600 dark:text-brand-400'
        };
      case 'STATUS_CHANGED':
        return {
          icon: ArrowPathIcon,
          bgColor: 'bg-orange-100 dark:bg-orange-900/20',
          iconColor: 'text-orange-600 dark:text-orange-400'
        };
      default:
        return {
          icon: ClockIcon,
          bgColor: 'bg-gray-100 dark:bg-slate-900/20',
          iconColor: 'text-gray-600 dark:text-gray-400'
        };
    }
  };

  // 格式化時間
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '-' : date.toLocaleString('zh-TW');
  };

  if (isLoading) {
    return (
      <div className={`${className}`}>
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-gray-200 dark:bg-slate-700 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className}`}>
        <div className="text-center py-8">
          <XCircleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">載入失敗</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <button
            onClick={() => fetchAuditLogs(periodClosingId)}
            className="mt-4 bg-brand-600 hover:bg-brand-700 text-white font-medium py-2 px-4 rounded transition-all duration-200"
          >
            重新載入
          </button>
        </div>
      </div>
    );
  }

  if (currentAuditLogs.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="text-center py-8">
          <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">暫無歷史記錄</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            當有結算活動時，會在這裡顯示歷史軌跡
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="flow-root pl-2 pt-4">
        <ul role="list" className="">
          {currentAuditLogs.map((log, logIdx) => {
            const { icon: Icon, bgColor, iconColor } = getActionIcon(log.action);

            return (
              <li key={log.id}>
                <div className="relative pb-8">
                  {logIdx !== currentAuditLogs.length - 1 ? (
                    <span
                      className="absolute top-4 left-6 -ml-px h-full w-0.5 bg-gray-200 dark:bg-slate-700"
                      aria-hidden="true"
                    />
                  ) : null}
                  <div className="relative flex space-x-3">
                    <div>
                      <span className={`${bgColor} h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white dark:ring-gray-800`}>
                        <Icon className={`h-5 w-5 ${iconColor}`} aria-hidden="true" />
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                      <div>
                        <p className="text-sm text-gray-900 dark:text-white font-medium">
                          {log.description}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          執行者：{log.performedBy}
                        </p>
                        {log.previousData && log.newData && (
                          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            <details>
                              <summary className="cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                                查看變更詳情
                              </summary>
                              <div className="mt-2 p-2 bg-gray-50 dark:bg-slate-700/50 rounded text-xs">
                                <div className="mb-2">
                                  <span className="font-medium">變更前：</span>
                                  <pre className="mt-1 whitespace-pre-wrap">
                                    {typeof log.previousData === 'object'
                                      ? JSON.stringify(log.previousData, null, 2)
                                      : log.previousData}
                                  </pre>
                                </div>
                                <div>
                                  <span className="font-medium">變更後：</span>
                                  <pre className="mt-1 whitespace-pre-wrap">
                                    {typeof log.newData === 'object'
                                      ? JSON.stringify(log.newData, null, 2)
                                      : log.newData}
                                  </pre>
                                </div>
                              </div>
                            </details>
                          </div>
                        )}
                      </div>
                      <div className="whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400 pr-4">
                        {formatTime(log.performedAt)}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PeriodClosingTimeline;